<script setup lang="ts">
defineOptions({ name: "AppNavbar" });
import { computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessageBox } from "element-plus";
import { useCurrentUserStore } from "@app/store/modules/currentUser";
import { useAuthStore } from "@shared/store/modules/auth";
import { logout as logoutApi } from "@shared/api/auth";
import { clearDynamicRoutes } from "@shared/router/installGuards";
// import { APP_TARGET } from "@shared/utils/storage";
import { t } from "@shared/utils/i18n";
import InSiteMessageBell from "./InSiteMessageBell.vue";
import TagsView from "../TagsView/index.vue";

const router = useRouter();
const auth = useAuthStore();
const currentUser = useCurrentUserStore();

/** 顶栏展示名：优先 /user/info 的 username */
const displayName = computed(() => {
    const detail = currentUser.userDetail;
    const fromInfo = detail?.username || detail?.nick || detail?.tel || "";
    return fromInfo || auth.name || t("未命名");
});

/** 有头像 URL 则展示图片，否则沿用现有图标 */
const avatarUrl = computed(() => {
    const fromDetail = (currentUser.userDetail?.avatar || "").trim();
    if (fromDetail) return fromDetail;
    return (auth.avatar || "").trim();
});

/** 站内信接口暂下线，顶栏铃铛先隐藏（原：仅 manage 展示） */
const showInSiteBell = false;
// const showInSiteBell = APP_TARGET === "manage";

async function handleLogout() {
    try {
        await ElMessageBox.confirm(t("确定要退出登录吗？"), t("提示"), {
            confirmButtonText: t("确定"),
            cancelButtonText: t("取消"),
            type: "warning",
        });
        try {
            await logoutApi();
        } catch (e) {
            console.error("[logout]", e);
        }
        await auth.logout();
        clearDynamicRoutes(router);
        router.push("/login");
    } catch {
        // 用户取消，无需处理
    }
}

function handleCommand(cmd: string) {
    if (cmd === "logout") {
        handleLogout();
    } else if (cmd === "profile") {
        router.push("/system/personalCenter");
    }
}
</script>

<template>
    <header class="navbar">
        <TagsView class="navbar-tags" />

        <div class="navbar-right">
            <InSiteMessageBell v-if="showInSiteBell" />
            <el-dropdown trigger="click" @command="handleCommand">
                <div class="user-info">
                    <el-avatar :size="24" class="user-avatar" :src="avatarUrl || undefined">
                        <i class="iconfont icon-user-owner" />
                    </el-avatar>
                    <span class="user-name">{{ displayName }}</span>
                    <i class="iconfont icon-chevron-down user-arrow" />
                </div>
                <template #dropdown>
                    <el-dropdown-menu>
                        <el-dropdown-item command="profile"> <i class="iconfont icon-user-owner dropdown-icon" />{{ t("个人中心") }} </el-dropdown-item>
                        <el-dropdown-item divided command="logout"> <i class="iconfont icon-logout dropdown-icon" />{{ t("退出登录") }} </el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
        </div>
    </header>
</template>

<style lang="scss" scoped>
.navbar {
    display: flex;
    align-items: center;
    height: var(--layout-header-height);
    padding: 0 12px 0 0;
    background-color: var(--main-background);
}

.navbar-tags {
    flex: 1;
    min-width: 0;
}

.navbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-left: 12px;
    height: 100%;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 8px;
    border-radius: var(--radius-base);
    cursor: pointer;
    user-select: none;

    &:hover {
        background-color: var(--main-background);
    }

    .user-name {
        font-size: var(--font-size-body);
        line-height: var(--line-height-body);
        color: var(--main-color-hover);
    }

    .user-arrow {
        font-size: 12px;
        line-height: 1;
        color: var(--main-color-active);
    }
}

.user-avatar {
    // background-color: var(--primary);
    color: #fff;

    .iconfont {
        font-size: 14px;
        line-height: 1;
    }
}

:deep(.dropdown-icon) {
    margin-right: 8px;
    font-size: 14px;
    line-height: 1;
}
</style>
